import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useStytch, useStytchSession } from "@stytch/react";
import { useCurrentUser, User } from "@/lib/api/user.ts";
import { AbilityProvider } from "@/lib/casl/ability-context";
import { Session } from "@stytch/vanilla-js";
import { useQueryClient } from "@tanstack/react-query";
import OneSignal from "react-onesignal";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticating: boolean;
  revokeSession: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>({
  user: null,
  session: null,
  isAuthenticating: true,
  revokeSession: () => Promise.resolve(),
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { session } = useStytchSession();
  const queryClient = useQueryClient();
  const { data: currentUser, isLoading } = useCurrentUser();
  const stytch = useStytch();

  const [initialized, setInitialized] = useState(false);

  const revokeSession = async () => {
    await stytch.session.revoke();
    queryClient.removeQueries({
      queryKey: ["current-user"],
    });
    await OneSignal.logout();
  };

  useEffect(() => {
    if (initialized || isLoading || !currentUser) {
      return;
    }

    OneSignal.init({
      appId: import.meta.env.VITE_ONESIGNAL_APP_ID as string,
      allowLocalhostAsSecureOrigin: true,
    }).then(() => {
      setInitialized(true);
      OneSignal.login(currentUser.id).then(() => {
        if (currentUser.phone) OneSignal.User.addSms(currentUser.phone);
        if (currentUser.email) OneSignal.User.addEmail(currentUser.email);
      });
    });
  }, [initialized, isLoading, currentUser]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticating: isLoading,
        user: isLoading || !currentUser ? null : currentUser,
        session,
        revokeSession,
      }}
    >
      <AbilityProvider rules={currentUser?.abilities}>
        {children}
      </AbilityProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
